import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  BUILDING_INFO,
  jobRemainingMs,
  PRODUCTION_ITEM_BY_KEY,
  RESEARCH_NODE_BY_ID,
  speedUpDiamondCost,
  type BuildingFamily,
} from "@stahlfront/core";
import {
  fetchConstructionList,
  fetchProductionList,
  fetchResearchList,
  speedupBuilding,
  speedupProduction,
  speedupResearch,
} from "../api.js";
import { useStore } from "../store.js";

/**
 * Phase 2.D — Speed-Up confirmation modal.
 *
 * Shared by §6.2 production slot Speed-Up button and §6.5 mid-upgrade
 * banner Speed-Up button. Opens when `speedupConfirm` slot is non-null.
 * Live ETA + live cost (1Hz interval); confirm dispatches the matching
 * POST endpoint and closes itself on success.
 */
export function SpeedUpConfirmModal(): JSX.Element | null {
  const target = useStore((s) => s.speedupConfirm);
  const setTarget = useStore((s) => s.setSpeedupConfirm);
  const productionJobs = useStore((s) => s.productionJobs);
  const constructionJobs = useStore((s) => s.constructionJobs);
  const researchJobs = useStore((s) => s.researchJobs);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const setProductionJobs = useStore((s) => s.setProductionJobs);
  const setConstructionJobs = useStore((s) => s.setConstructionJobs);
  const setResearchJobs = useStore((s) => s.setResearchJobs);
  const setResearchSlots = useStore((s) => s.setResearchSlots);
  const setCompletedNodeIds = useStore((s) => s.setCompletedNodeIds);

  const [pending, setPending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  /** Forces a re-render every second so cost + ETA stay live. */
  const [, setTick] = useState(0);

  // 1Hz interval — live ETA and live cost. Cleared on close.
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [target]);

  // Esc to close (when not in flight).
  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !pending) setTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, pending, setTarget]);

  const close = useCallback((): void => {
    if (pending) return;
    setTarget(null);
    setErrorText(null);
  }, [pending, setTarget]);

  // Resolve job + family + display string for the title bar.
  const ctx = useMemo(() => {
    if (!target) return null;
    if (target.kind === "production") {
      const job = productionJobs.find((j) => j.id === target.jobId);
      if (!job) return null;
      const item = PRODUCTION_ITEM_BY_KEY[job.itemKey];
      const family = job.family as BuildingFamily;
      return {
        family,
        displayName: item?.displayName ?? job.itemKey,
        startedAtMs: job.startedAtMs,
        durationMs: job.durationMs,
        rowLabel: "Job",
      } as const;
    }
    if (target.kind === "research") {
      const job = researchJobs.find((j) => j.id === target.jobId);
      if (!job) return null;
      const node = RESEARCH_NODE_BY_ID[job.nodeId];
      // RESEARCH_CENTER hue (231°) for the family-accent border.
      const family: BuildingFamily = "RESEARCH_CENTER";
      return {
        family,
        displayName: node?.displayName ?? job.nodeId,
        startedAtMs: job.startedAtMs,
        durationMs: job.durationMs,
        rowLabel: "Research",
      } as const;
    }
    const job = constructionJobs.find((j) => j.id === target.jobId);
    if (!job) return null;
    const family = job.family as BuildingFamily;
    const meta = BUILDING_INFO[family];
    return {
      family,
      displayName: `${meta.displayName} → Lv ${job.targetLevel}`,
      startedAtMs: job.startedAtMs,
      durationMs: job.durationMs,
      rowLabel: "Upgrade",
    } as const;
  }, [target, productionJobs, constructionJobs, researchJobs]);

  if (!target || !ctx) return null;

  const meta = BUILDING_INFO[ctx.family];
  const accent = `oklch(72% 0.14 220)`;
  const accentTitle = `oklch(28% 0.08 220)`;
  const familyAccent = `oklch(58% 0.16 ${meta.accentHue})`;

  const remainingMs = jobRemainingMs(ctx.startedAtMs, ctx.durationMs, Date.now());
  const cost = speedUpDiamondCost(remainingMs);
  const diamonds = me?.wallet.diamonds ?? 0;
  const canAfford = diamonds >= cost;
  const shortfall = canAfford ? 0 : cost - diamonds;

  const onConfirm = async (): Promise<void> => {
    if (pending || !canAfford || cost <= 0 || !me) return;
    setPending(true);
    setErrorText(null);
    try {
      if (target.kind === "production") {
        const res = await speedupProduction({ jobId: target.jobId });
        setMe({ ...me, wallet: res.wallet });
        const list = await fetchProductionList(me.base.id);
        setProductionJobs(list.jobs);
      } else if (target.kind === "research") {
        const res = await speedupResearch({ jobId: target.jobId });
        setMe({ ...me, wallet: res.wallet });
        const list = await fetchResearchList();
        setResearchJobs(list.activeJobs);
        setCompletedNodeIds(list.completedNodeIds);
        setResearchSlots(list.slots);
      } else {
        const res = await speedupBuilding({ jobId: target.jobId });
        setMe({ ...me, wallet: res.wallet });
        const list = await fetchConstructionList(me.base.id);
        setConstructionJobs(list.jobs);
      }
      setTarget(null);
    } catch (err) {
      const m = String(err);
      if (m.includes("INSUFFICIENT_DIAMONDS")) setErrorText("Not enough diamonds.");
      else if (m.includes("ALREADY_COMPLETE")) setErrorText("Job already finished — refresh.");
      else if (m.includes("JOB_NOT_ACTIVE")) setErrorText("Job no longer active.");
      else setErrorText("Couldn't apply Speed-Up. Try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="spd-backdrop"
      onPointerDown={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spd-title"
    >
      <div
        className="spd-card"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          ["--spd-accent" as string]: accent,
          ["--spd-accent-title" as string]: accentTitle,
          ["--spd-family-accent" as string]: familyAccent,
        }}
      >
        <header className="spd-titlebar">
          <span id="spd-title" className="spd-title">
            Speed up · {ctx.displayName}
          </span>
          <button
            type="button"
            className="spd-close"
            aria-label="Close speed-up dialog"
            onClick={close}
          >
            ×
          </button>
        </header>

        <section className="spd-rows" aria-label="Speed-up preview">
          <div className="spd-row">
            <span className="spd-row-label">{ctx.rowLabel}</span>
            <span className="spd-row-value">{ctx.displayName}</span>
          </div>
          <div className="spd-row">
            <span className="spd-row-label">Time remaining</span>
            <span className="spd-row-value spd-eta" aria-live="polite">
              {formatDuration(remainingMs)}
            </span>
          </div>
          <div className="spd-row">
            <span className="spd-row-label">Diamond cost</span>
            <span
              className={`spd-row-value spd-cost ${canAfford ? "" : "spd-row-value-bad"}`}
              key={cost}
              aria-live="polite"
            >
              <span className="spd-glyph" aria-hidden="true">◆</span>
              {fmt(cost)}
            </span>
          </div>
          <div className="spd-row">
            <span className="spd-row-label">Your balance</span>
            <span className="spd-row-value">
              <span className="spd-glyph" aria-hidden="true">◆</span>
              {fmt(diamonds)}
              {!canAfford ? (
                <span className="spd-shortfall">need ◆{fmt(shortfall)} more</span>
              ) : null}
            </span>
          </div>
        </section>

        {errorText ? (
          <p className="spd-error" role="alert">
            {errorText}
          </p>
        ) : null}

        <footer className="spd-footer">
          <button
            type="button"
            className="spd-btn spd-btn-secondary"
            onClick={close}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="spd-btn spd-btn-primary"
            onClick={() => void onConfirm()}
            disabled={pending || !canAfford || cost <= 0}
            aria-busy={pending}
          >
            {pending ? (
              <>
                <span className="spd-spinner" aria-hidden="true" /> Applying…
              </>
            ) : remainingMs <= 0 ? (
              "Already done"
            ) : canAfford ? (
              <>Confirm · ◆{fmt(cost)}</>
            ) : (
              <>Need ◆{fmt(shortfall)} more</>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

function fmt(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)} k`;
  return new Intl.NumberFormat("en-US").format(value);
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const sec = Math.ceil(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}
